import React from 'react';

interface PrimarySwitchProps {
  size?: 'sm' | 'md' | 'lg';
  isChecked: boolean;
  onChanged: (checked: boolean) => void;
  disabled?: boolean;
}

const PrimarySwitch: React.FC<PrimarySwitchProps> = ({
  size = 'md',
  isChecked,
  onChanged,
  disabled = false
}) => {
  const getSizeConfig = () => {
    switch (size) {
      case 'sm':
        return {
          trackWidth: 28,
          trackHeight: 14,
          thumbSize: 12,
          borderRadius: 7,
          thumbOffset: 12  // trackWidth(28) - thumbSize(12) - rightPadding(1) = 15
        };
      case 'md':
        return {
          trackWidth: 32,
          trackHeight: 16,
          thumbSize: 14,
          borderRadius: 8,
          thumbOffset: 15  // trackWidth(32) - thumbSize(14) - rightPadding(1) = 17
        };
      case 'lg':
        return {
          trackWidth: 36,
          trackHeight: 18,
          thumbSize: 16,
          borderRadius: 9,
          thumbOffset: 17  // trackWidth(36) - thumbSize(16) - rightPadding(1) = 19
        };
      default:
        return {
          trackWidth: 32,
          trackHeight: 16,
          thumbSize: 14,
          borderRadius: 8,
          thumbOffset: 15
        };
    }
  };

  const sizeConfig = getSizeConfig();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChanged(e.target.checked);
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'inline-block',
    verticalAlign: 'middle',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };

    const trackStyle: React.CSSProperties = {
    width: sizeConfig.trackWidth,
    height: sizeConfig.trackHeight,
    borderRadius: sizeConfig.borderRadius,
    position: 'relative',
    transition: 'background-color 0.2s ease',
    border: '2px solid transparent',
  };

  const thumbStyle: React.CSSProperties = {
    width: sizeConfig.thumbSize,
    height: sizeConfig.thumbSize,
    borderRadius: '50%',
    position: 'absolute',
    top: '50%',
    transform: `translateY(-50%) translateX(${isChecked ? sizeConfig.thumbOffset : 0}px)`,
    transition: 'transform 0.2s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  };

  const hiddenInputStyle: React.CSSProperties = {
    position: 'absolute',
    opacity: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    cursor: 'inherit',
  };

  return (
    <div style={containerStyle}>
      <div
        style={trackStyle}
        className={`
          ${isChecked
            ? 'bg-primary-light dark:bg-primary-dark'
            : 'bg-gray-300 dark:bg-gray-600'
          }
          focus-within:shadow-lg focus-within:shadow-primary-light/20
        `}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
          aria-label="Toggle switch"
          style={hiddenInputStyle}
        />
        <div
          style={thumbStyle}
          className="bg-white dark:bg-gray-200"
        />
      </div>
    </div>
  );
};

export default PrimarySwitch;
